import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Check, ArrowRight, Loader2, CreditCard } from "lucide-react";

const PAY_METHODS = [
  { id: "razorpay",      label: "Razorpay (Cards / UPI / NetBanking)" },
  { id: "upi",           label: "UPI (manual transaction ID)" },
  { id: "imps",          label: "IMPS" },
  { id: "neft",          label: "NEFT" },
  { id: "rtgs",          label: "RTGS" },
  { id: "bank_transfer", label: "Bank transfer" },
  { id: "pos",           label: "POS swipe" },
  { id: "cash",          label: "Cash" },
  { id: "other",         label: "Other" },
];

export default function PlanCheckout() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [method, setMethod] = useState("razorpay");
  const [details, setDetails] = useState({ transaction_id: "", bank_rrn: "", payment_ref: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/subscription/plans").then((r) => {
      setPlans(r.data.plans);
      setChosen(r.data.plans[1]?.id || r.data.plans[0]?.id); // default to Professional
    });
  }, []);

  const submit = async () => {
    if (!chosen) return;
    setSubmitting(true);
    try {
      await api.post("/subscription/choose", {
        plan_id: chosen, payment_method: method,
        transaction_id: details.transaction_id.trim() || null,
        bank_rrn: details.bank_rrn.trim() || null,
        payment_ref: details.payment_ref.trim() || null,
        notes: details.notes.trim() || null,
      });
      toast.success("Payment recorded. Awaiting admin verification.");
      nav("/onboarding/pending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const sel = plans.find((p) => p.id === chosen);

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg p-6 lg:p-12">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <Crown className="h-3.5 w-3.5" /> Step 3 of 3 · Choose your plan
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Pick a plan to unlock your AI agency</h1>
          <p className="text-white/50 mt-2">Every plan runs for 30 days from admin approval. You can change anytime.</p>
        </header>

        <section className="grid md:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
          {plans.map((p) => {
            const active = p.id === chosen;
            return (
              <button key={p.id} onClick={() => setChosen(p.id)}
                data-testid={`plan-${p.id}`}
                className={`glass text-left rounded-2xl p-5 transition tilt-card ${active ? "border-indigo-500/60 ring-1 ring-indigo-500/40 bg-indigo-500/[0.05]" : "hover:border-white/20"}`}>
                <div className="font-heading font-black text-xl">{p.name}</div>
                <div className="mt-3 font-heading font-black text-3xl">₹{p.amount.toLocaleString("en-IN")}</div>
                <div className="text-xs text-white/40">/ 30 days</div>
                <div className="text-xs text-white/60 mt-3 leading-relaxed">{p.description}</div>
                {active && <div className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-300"><Check className="h-3 w-3" /> Selected</div>}
              </button>
            );
          })}
        </section>

        <div className="glass rounded-2xl p-6 lg:p-8" data-testid="payment-form">
          <h3 className="font-heading text-lg font-black mb-4 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-indigo-300" /> Payment details
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Lab label="Payment method *">
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm">
                {PAY_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </Lab>
            <Lab label="Transaction ID / UTR">
              <input value={details.transaction_id} onChange={(e) => setDetails({ ...details, transaction_id: e.target.value })}
                data-testid="payment-txn" placeholder="UPI/2025/0001..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm font-mono" />
            </Lab>
            <Lab label="Bank RRN (optional)">
              <input value={details.bank_rrn} onChange={(e) => setDetails({ ...details, bank_rrn: e.target.value })}
                data-testid="payment-rrn"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm font-mono" />
            </Lab>
            <Lab label="Payment reference (optional)">
              <input value={details.payment_ref} onChange={(e) => setDetails({ ...details, payment_ref: e.target.value })}
                data-testid="payment-ref"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm font-mono" />
            </Lab>
            <Lab label="Notes (optional)" full>
              <textarea rows={3} value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                data-testid="payment-notes"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm" />
            </Lab>
          </div>

          <div className="mt-6 p-4 glass rounded-xl flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-white/40 uppercase tracking-[0.2em]">Order summary</div>
              <div className="font-heading font-black text-2xl mt-1">{sel?.name} · ₹{sel?.amount?.toLocaleString("en-IN")}</div>
              <div className="text-xs text-white/50 mt-1">30 days · invoice will be emailed</div>
            </div>
            <button onClick={submit} disabled={submitting || !sel} data-testid="payment-submit"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Submit for admin verification
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lab({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-white/40 uppercase tracking-[0.2em]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
